/* import __COLOCATED_TEMPLATE__ from './schedule-delivery-drawer.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { cached } from 'tracked-toolbox';
import moment from 'moment-timezone';
import { isEmpty, isPresent } from '@ember/utils';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { type TaskGenerator } from 'ember-concurrency';
import type IntlService from 'ember-intl/services/intl';
import type Store from '@ember-data/store';
import type AppService from 'embercom/services/app-service';
import type DatasetExportScheduleService from 'embercom/services/dataset-export-schedule-service';
import type NotificationsService from 'embercom/services/notifications-service';
import type Report from 'embercom/models/reporting/custom/report';
import type ScheduledExternalShare from 'embercom/models/reporting/scheduled-external-share';
import Admin from 'embercom/models/admin';
import Role from 'embercom/models/role';
import { SchedulePreset, type ExportRule } from 'embercom/models/reporting/dataset-export-schedule';
import { ScheduleFrequencyHelpers } from 'embercom/utils/schedule-frequency-helpers';
import {
  getAvailableTeammates,
  getAvailableRoles,
  getSuggestedTeammates,
  getSuggestedRoles,
  getSuggestedEntities,
  isTeammate,
  getRoleSize,
  extractEntitiesFromRecipients,
  selectEntity,
  removeEntity,
  removeEmail,
  handleInput,
  handleEnter,
  handleBackspace,
  type Entity,
} from 'embercom/utils/recipient-selector-helpers';
import type { SchedulePayload } from 'embercom/types/reporting/scheduled-delivery';
import { validateAndSeparateEmails } from 'embercom/utils/email-helpers';
import ManageSchedulesApi from 'embercom/lib/reporting/manage-schedules-api';
import ScheduleFormState from 'embercom/lib/reporting/schedule-form-state';

interface Args {
  report?: Report;
  schedule?: ScheduledExternalShare;
  isOpen: boolean;
  onClose: () => void;
  onScheduleUpdated?: (updatedSchedule: ScheduledExternalShare) => void;
}

interface Signature {
  Element: HTMLDivElement;
  Args: Args;
}

export default class ReportingCustomReportScheduleDeliveryDrawer extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare appService: AppService;
  @service declare datasetExportScheduleService: DatasetExportScheduleService;
  @service declare intercomEventService: $TSFixMe;
  @service declare notificationsService: InstanceType<typeof NotificationsService>;

  formState = new ScheduleFormState();
  scheduleApi: ManageSchedulesApi;

  @tracked selectedEntities: Entity[] = [];
  @tracked rolesList: Role[] = [];
  @tracked rolesLoaded = false;
  @tracked userInput = '';
  @tracked focusActive = false;
  @tracked shouldRefocusOnPopoverHide = true;
  @tracked declare lastAddedEntity?: Entity;
  @tracked selectedEmails: string[] = [];
  @tracked currentTime = new Date();
  @tracked scheduleFromList?: ScheduledExternalShare;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.scheduleApi = new ManageSchedulesApi(owner);
    this.formState.exportRule = this.getDefaultExportRule();
    this.startTimeUpdater();
  }

  private timeUpdaterInterval?: NodeJS.Timeout;

  private isOneMinuteFrequencyEnabled(): boolean {
    return localStorage.getItem('enable-schedule-delivery-one-minute') === 'true';
  }

  private startTimeUpdater() {
    if (this.timeUpdaterInterval) {
      clearInterval(this.timeUpdaterInterval);
    }

    if (this.isOneMinuteFrequencyEnabled()) {
      this.timeUpdaterInterval = setInterval(() => {
        this.currentTime = new Date();
      }, 1000);
    }
  }

  willDestroy() {
    super.willDestroy();
    if (this.timeUpdaterInterval) {
      clearInterval(this.timeUpdaterInterval);
    }
  }

  sharedAnalyticsData = {
    object: 'schedule_delivery',
    section: 'reports',
  };

  get isCreateDisabled() {
    if (!this.isScheduleNameValid) {
      return true;
    }
    if (this.isScheduleMessageTooLong) {
      return true;
    }
    if (
      this.formState.userIds.length === 0 &&
      this.formState.roleIds.length === 0 &&
      this.formState.emails.length === 0
    ) {
      return true;
    }
    if (!this.formState.hasUnsavedChanges(this.isEditMode)) {
      return true;
    }
    return false;
  }

  get isCreatingSchedule(): boolean {
    return taskFor(this.createScheduleTask).isRunning;
  }

  get isEditMode(): boolean {
    return !!this.args.schedule || !!this.scheduleFromList;
  }

  get isEditingFromList(): boolean {
    return !!this.scheduleFromList;
  }

  get activeSchedule(): ScheduledExternalShare | undefined {
    return this.args.schedule ?? this.scheduleFromList;
  }

  private readonly MAX_SCHEDULE_NAME_LENGTH = 255;
  private readonly MAX_SCHEDULE_MESSAGE_LENGTH = 1000;

  get isScheduleNameValid(): boolean {
    return (
      this.formState.scheduleName.length <= this.MAX_SCHEDULE_NAME_LENGTH &&
      !isEmpty(this.formState.scheduleName.trim())
    );
  }

  get scheduleNameCharactersRemaining(): number {
    return this.MAX_SCHEDULE_NAME_LENGTH - this.formState.scheduleName.length;
  }

  get isScheduleNameTooLong(): boolean {
    return this.formState.scheduleName.length > this.MAX_SCHEDULE_NAME_LENGTH;
  }

  get scheduleMessageCharactersRemaining(): number {
    return this.MAX_SCHEDULE_MESSAGE_LENGTH - this.formState.scheduleMessage.length;
  }

  get isScheduleMessageTooLong(): boolean {
    return this.formState.scheduleMessage.length > this.MAX_SCHEDULE_MESSAGE_LENGTH;
  }

  get reportId(): number | undefined {
    let id = this.args.report?.id ?? this.args.schedule?.reportId;
    return id ? parseInt(String(id), 10) : undefined;
  }

  get showReportSchedules(): boolean {
    return (
      !this.isEditMode &&
      (this.appService.app?.canUseFeature('reporting-show-report-schedules') ?? false)
    );
  }

  get hasCustomDateRange(): boolean {
    return this.args.report?.dateRange?.selection === 'custom';
  }

  @cached
  get timezone(): string {
    return this.args.report?.timezone || this.appService.app?.timezone || 'UTC';
  }

  @cached
  get app() {
    return this.appService.app;
  }

  @cached
  get humanAdmins(): Admin[] {
    return (this.app?.humanAdmins as Admin[] | undefined) || [];
  }

  @cached
  get selectedTeammates(): Admin[] {
    return this.selectedEntities.filter((entity): entity is Admin => entity instanceof Admin);
  }

  @cached
  get selectedRoles(): Role[] {
    return this.selectedEntities.filter((entity): entity is Role => entity instanceof Role);
  }

  @cached
  get availableTeammates() {
    return getAvailableTeammates(this.humanAdmins, this.selectedTeammates);
  }

  @cached
  get availableRoles() {
    return getAvailableRoles(this.rolesList, this.selectedRoles);
  }

  @cached
  get suggestedTeammates() {
    return getSuggestedTeammates(this.availableTeammates, this.userInput);
  }

  @cached
  get suggestedRoles() {
    return getSuggestedRoles(this.availableRoles, this.userInput);
  }

  @cached
  get suggestedEntities() {
    return getSuggestedEntities(this.suggestedRoles, this.suggestedTeammates);
  }

  @cached
  get hasSuggestedTeammates() {
    return this.suggestedTeammates.length > 0;
  }

  @cached
  get hasSuggestedRoles() {
    return this.suggestedRoles.length > 0;
  }

  @cached
  get disableSuggestion() {
    return !this.hasSuggestedTeammates && !this.hasSuggestedRoles;
  }

  @cached
  get placeHolder() {
    return this.intl.t('reporting.schedule-delivery.recipients-placeholder');
  }

  getDefaultExportRule(): ExportRule {
    // Default to "Every Monday"
    return this.datasetExportScheduleService.exportRuleFrom(
      SchedulePreset.EVERY_MONDAY,
      this.timezone,
    );
  }

  get frequencyItems() {
    if (this.isOneMinuteFrequencyEnabled()) {
      void this.currentTime;
    }

    return ScheduleFrequencyHelpers.getFrequencyPresetItems(
      this.intl,
      'reporting.schedule-delivery',
      this.formState.exportRule!,
      this.timezone,
      this.datasetExportScheduleService,
      (preset) => this.updateExportRuleForPreset(preset),
    );
  }

  get isCustomSelected() {
    return ScheduleFrequencyHelpers.isCustomFrequencySelected(this.frequencyItems);
  }

  get isPreset() {
    return !this.isCustomSelected;
  }

  @cached
  get selectedFrequencyLabel(): string {
    let scheduleObj = {
      exportRule: this.formState.exportRule,
      timezone: this.timezone,
    };
    return this.datasetExportScheduleService.customLabel(scheduleObj);
  }

  @cached
  get hours() {
    return ScheduleFrequencyHelpers.getHours(this.timezone, moment);
  }

  get selectedTime(): number {
    return this.formState.exportRule?.deliveryTime?.hour ?? 0;
  }

  @action
  updateExportRuleForPreset(preset: SchedulePreset) {
    let newRule = this.datasetExportScheduleService.exportRuleFrom(preset, this.timezone);
    this.formState.exportRule = {
      ...newRule,
      deliveryTime: this.formState.exportRule?.deliveryTime || newRule.deliveryTime,
    };
  }

  @action
  onApplyCustomFrequency(rule: ExportRule) {
    this.formState.exportRule = rule;
  }

  @action
  updateTime(hour: number) {
    if (!this.formState.exportRule) {
      return;
    }
    this.formState.exportRule = {
      ...this.formState.exportRule,
      deliveryTime: {
        ...this.formState.exportRule.deliveryTime,
        hour,
      },
    };
  }

  @action
  updateScheduleName(event: Event) {
    this.formState.scheduleName = (event.target as HTMLInputElement).value;
  }

  @action
  updateScheduleMessage(event: Event) {
    this.formState.scheduleMessage = (event.target as HTMLTextAreaElement).value;
  }

  @action
  async onDrawerOpen() {
    // Reset any previous edit-from-list state when drawer opens fresh
    this.scheduleFromList = undefined;

    this.intercomEventService.trackAnalyticsEvent({
      ...this.sharedAnalyticsData,
      action: 'opened_drawer',
      report_id: this.args.report?.id,
    });

    if (this.args.report?.hasDirtyAttributes) {
      this.args.report.rollbackAttributes();
    }

    await this.ensureRolesLoaded();
    this.initializeFormFields();
  }

  initializeFormFields() {
    let schedule = this.activeSchedule;

    if (schedule) {
      this.formState.initializeFromSchedule(schedule);
      this.populateRecipients(schedule);
      this.syncRecipientsToFormState();
    } else {
      this.formState.initializeForCreate(this.getDefaultExportRule());
      this.selectedEntities = [];
      this.selectedEmails = [];
      this.syncRecipientsToFormState();
    }
  }

  populateRecipients(schedule: ScheduledExternalShare) {
    let { entities, emails } = extractEntitiesFromRecipients(
      schedule.recipients,
      this.humanAdmins,
      this.rolesList,
    );
    this.selectedEntities = entities;
    this.selectedEmails = emails;
  }

  async ensureRolesLoaded() {
    if (this.rolesLoaded) {
      return;
    }

    try {
      let roles = await this.store.findAll('role');
      this.rolesList = roles.toArray();
      this.rolesLoaded = true;
    } catch (error) {
      // Fail gracefully - allow drawer to function without roles
      // Users can still add teammates and email addresses
      this.rolesList = [];
      this.rolesLoaded = true;
    }
  }

  @action
  isTeammateEntity(entity: Entity) {
    return isTeammate(entity);
  }

  @action
  getSize(entity: Entity) {
    return getRoleSize(entity);
  }

  private syncRecipientsToFormState(): void {
    let userIds = this.selectedTeammates.map((admin) => admin.id);
    let roleIds = this.selectedRoles.map((role) => role.id);
    this.formState.updateRecipients(userIds, roleIds, this.selectedEmails);
  }

  @action
  selectEntity(entity: Entity) {
    let result = selectEntity(this.selectedEntities, entity);
    this.selectedEntities = result.entities;
    this.lastAddedEntity = result.lastAdded;
    this.syncRecipientsToFormState();

    // Track recipient addition
    this.intercomEventService.trackAnalyticsEvent({
      ...this.sharedAnalyticsData,
      action: 'added_recipient',
      recipient_type: entity instanceof Admin ? 'teammate' : 'role',
    });
  }

  @action
  removeEntity(selectedEntity: Entity) {
    this.selectedEntities = removeEntity(this.selectedEntities, selectedEntity);
    this.syncRecipientsToFormState();
  }

  @action
  removeEmail(email: string) {
    this.selectedEmails = removeEmail(this.selectedEmails, email);
    this.syncRecipientsToFormState();
  }

  @action
  handleInput(
    popover: { hide: () => void; show: () => void },
    e: InputEvent & { target: HTMLInputElement },
  ) {
    this.userInput = handleInput(popover, e);
  }

  @action
  handleEnter(popover: { hide: () => void; show: () => void }) {
    let result = handleEnter(
      popover,
      this.userInput,
      this.suggestedEntities,
      (entity) => this.selectEntity(entity),
      (email) => {
        if (!this.selectedEmails.includes(email)) {
          this.selectedEmails = [...this.selectedEmails, email];
          this.syncRecipientsToFormState();
        }
      },
    );

    if (result.shouldClearInput) {
      this.userInput = '';
    }
  }

  @action
  handleBackspace() {
    let result = handleBackspace(this.userInput, this.selectedEntities, this.selectedEmails);
    this.selectedEntities = result.entities;
    this.selectedEmails = result.emails;
    this.syncRecipientsToFormState();
  }

  @action
  onClickSuggestion(popover: { hide: () => void; show: () => void }, entity: Entity) {
    this.selectEntity(entity);
    this.userInput = '';
    this.shouldRefocusOnPopoverHide = true;
    popover.hide();
  }

  @action
  focusOnInput() {
    if (this.shouldRefocusOnPopoverHide) {
      this.focusActive = true;
    }
    this.shouldRefocusOnPopoverHide = true;
  }

  @action
  handleBlur() {
    if (isPresent(this.userInput)) {
      let { validEmails, invalidParts } = validateAndSeparateEmails(this.userInput);

      if (validEmails.length > 0) {
        let newEmails = validEmails.filter((email) => !this.selectedEmails.includes(email));
        if (newEmails.length > 0) {
          this.selectedEmails = [...this.selectedEmails, ...newEmails];
          this.syncRecipientsToFormState();
        }
      }

      this.userInput = invalidParts.join(' ').trim();
    }
    this.shouldRefocusOnPopoverHide = false;
  }

  @task({ drop: true })
  *createScheduleTask(): TaskGenerator<void> {
    let app = this.appService.app;
    let reportId = this.reportId;
    let scheduleId = this.activeSchedule?.id;

    if (this.isCreateDisabled || !reportId || !app) {
      return;
    }

    this.intercomEventService.trackAnalyticsEvent({
      ...this.sharedAnalyticsData,
      action: this.isEditMode ? 'update_schedule' : 'create_schedule',
      report_id: reportId,
      recipient_count: this.selectedEntities.length + this.selectedEmails.length,
      teammate_count: this.selectedTeammates.length,
      role_count: this.selectedRoles.length,
      email_count: this.selectedEmails.length,
    });

    try {
      if (!this.formState.exportRule) {
        return;
      }

      let payload: SchedulePayload = {
        appId: app.id,
        name: this.formState.scheduleName,
        reportId,
        message: this.formState.scheduleMessage,
        recipients: {
          userIds: this.formState.userIds.map((id) => parseInt(id, 10)),
          roles: this.formState.roleIds.map((id) => parseInt(id, 10)),
          emails: this.formState.emails,
          unsubscribedEmails: this.formState.unsubscribedEmails,
        },
        exportRule: this.formState.exportRule,
      };

      if (this.isEditMode && scheduleId) {
        let response = yield this.scheduleApi.updateSchedule(scheduleId, payload);

        this.notificationsService.notifyConfirmation(
          this.intl.t('reporting.schedule-delivery.schedule-updated'),
        );

        if (this.args.onScheduleUpdated) {
          this.args.onScheduleUpdated(response);
        }
      } else {
        yield this.scheduleApi.createSchedule(payload);

        this.notificationsService.notifyConfirmation(
          this.intl.t('reporting.schedule-delivery.schedule-created'),
        );
      }

      this.args.onClose();
    } catch (error: unknown) {
      let errorMessage = this.intl.t(
        this.isEditMode
          ? 'reporting.schedule-delivery.schedule-update-error'
          : 'reporting.schedule-delivery.schedule-creation-error',
      );

      if ((error as any)?.message && (error as any)?.name === 'ValidationError') {
        errorMessage = (error as any).message;
      }

      this.notificationsService.notifyResponseError(error as any, {
        default: errorMessage,
      });
    }
  }

  @action
  handleCreate() {
    taskFor(this.createScheduleTask).perform();
  }

  @action
  async handleEditScheduleFromList(schedule: ScheduledExternalShare) {
    this.scheduleFromList = schedule;
    await this.ensureRolesLoaded();
    this.initializeFormFields();
  }

  @action
  cancelEditFromList() {
    this.scheduleFromList = undefined;
    this.initializeFormFields();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Custom::Report::ScheduleDeliveryDrawer': typeof ReportingCustomReportScheduleDeliveryDrawer;
    'reporting/custom/report/schedule-delivery-drawer': typeof ReportingCustomReportScheduleDeliveryDrawer;
  }
}
